/**
 * @file NewPropertiesDocument.js
 * @description Properties panel for document container configuration.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import debounce from 'lodash/debounce';
import { FileDrop } from '../helpers/FileDrop';
import './PropertiesMultimedia.css';
import Toast from '../ui/toasts/Toast';
import { uploadFile } from '../../services/containerService';
import { useUploadContext } from '../../store/Contexts';
import { formatFileSize, sanitizeFileName } from '../helpers/FileUtils';
import {
    createAsset,
    fetchAssets,
    fetchAssetByID,
} from '../../services/clientService';
import { InputField, SwitchField } from '../helpers/FormComponents';
import Tooltip from '../ui/tooltips/Tooltip';
import TagInput from '../helpers/TagInput';
import styles from '../assets/AssetLibrary.module.scss';
import AssetGridView from '../assets/components/AssetGridView';
import PaginationComponent from '../helpers/PaginationComponent';
import NotFoundBox from '../ui/no_data_indicators/NotFoundBox';
import AddFromAssetsModal from '../ui/modals/AddFromAssetsModal';
import { isValidFileTypeAndSize } from '../../utils/validate';
import { Popover, Box } from '@mui/material';
import Spinner from '../ui/spinners/Spinner';
import { getText } from '../../i18n';

const SUPPORTED_DOCUMENT_TYPES = [
    'doc',
    'docx',
    'pdf',
    'txt',
    'xls',
    'xlsx',
    'csv',
    'ppt',
    'pptx',
];

const BLOCKED_DOCUMENT_EXTENSIONS = ['png', 'jpg', 'jpeg'];

function NewPropertiesDocument({
    item,
    setItem,
    handleUploadFromDevice,
    handleAddFromAssets,
    setIsSaving,
    documentState,
    setDocumentState,
}) {
    const [areInputsDisabled, setAreInputsDisabled] = useState(false);
    const [file, setFile] = useState(null);
    const { enqueueUpload } = useUploadContext();
    const [isAddFromAssetsChecked, setIsAddFromAssetsChecked] = useState('');
    const [isUploadFromDeviceChecked, setIsUploadFromDeviceChecked] =
        useState('');
    const [asset, setAsset] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [assets, setAssets] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [activeCard, setActiveCard] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [documentOpen, setDocumentOpen] = useState(false);
    const documentId = documentOpen ? 'simple-popover' : undefined;
    const [documentAnchorEl, setDocumentAnchorEl] = useState(null);
    const latestRequestIdRef = useRef(0);

    const { documentAsset, caption, assetName, isInitialized } = documentState;

    function updateDocumentState(updates) {
        setDocumentState(prev => ({ ...prev, ...updates }));
    }

    function handleDocumentPopoverClick(event) {
        setDocumentAnchorEl(event.currentTarget);
        setDocumentOpen(!documentOpen);
    }

    function handlePopoverClose() {
        setDocumentAnchorEl(null);
        setDocumentOpen(false);
    }

    useEffect(() => {
        handleUploadFromDevice(isUploadFromDeviceChecked);
    }, [isUploadFromDeviceChecked, handleUploadFromDevice]);

    useEffect(() => {
        handleAddFromAssets(isAddFromAssetsChecked);
    }, [isAddFromAssetsChecked, handleAddFromAssets]);

    useEffect(() => {
        setItem(prevState => {
            const { documentPart } = prevState;
            return {
                ...prevState,
                documentPart: {
                    ...documentPart,
                    url: '',
                    caption,
                    isExternal: true,
                },
                containerAssets: documentAsset.assetId ? [documentAsset] : [],
            };
        });
    }, [documentAsset, caption, setItem]);

    useEffect(() => {
        if (isInitialized) {
            return;
        }

        async function fetchInitial() {
            setIsLoading(true);

            if (item === undefined) {
                updateDocumentState({ isInitialized: true });
                setIsLoading(false);
                return;
            }

            if ('templateStatus' in item && item.templateStatus === -1) {
                setAreInputsDisabled(true);
            }

            let tempDocumentAsset = {};
            let tempAssetName = '';
            let tempCaption = '';

            if (item.documentPart?.caption) {
                tempCaption = item.documentPart.caption;
            }

            const hasContainerAssets =
                'containerAssets' in item &&
                item.containerAssets !== null &&
                item.containerAssets.length > 0;

            if (hasContainerAssets) {
                item.containerAssets = item.containerAssets.filter(
                    assetItem => Object.keys(assetItem).length > 0,
                );

                for (const containerAsset of item.containerAssets) {
                    if (!containerAsset.assetId) {
                        continue;
                    }

                    const fetchedAsset = await fetchAssetByID(
                        containerAsset.assetId,
                        null,
                    );

                    tempDocumentAsset = {
                        id: containerAsset.id,
                        assetId: containerAsset.assetId,
                    };
                    tempAssetName = fetchedAsset.name;
                    break;
                }
            }

            updateDocumentState({
                documentAsset: tempDocumentAsset,
                assetName: tempAssetName,
                caption: tempCaption,
                isInitialized: true,
            });

            setIsLoading(false);
        }

        fetchInitial();
    }, [isInitialized, item]);

    function handleFileChange(fileValue) {
        setFile(sanitizeFileName(fileValue));
    }

    function handleCaptionChange(event) {
        updateDocumentState({ caption: event.target.value });
    }

    async function uploadAsset() {
        setIsSaving(true);

        if (!validateForm()) {
            setIsSaving(false);
            return;
        }

        let urlResponse;
        let isValidUrl = false;

        if (!asset.url) {
            try {
                const response = await enqueueUpload(file.name, uploadOptions =>
                    uploadFile(file, uploadOptions),
                );
                if (response.error) {
                    Toast.error(response.message);
                    setIsSaving(false);
                    return;
                }
                urlResponse = encodeURI(response);
                try {
                    new URL(urlResponse);
                    isValidUrl = true;
                    setAsset(prev => ({
                        ...prev,
                        url: urlResponse,
                    }));
                } catch {
                    Toast.error(getText('msg_3e298875de25'));
                    setIsSaving(false);
                    return;
                }
            } catch {
                Toast.error(getText('msg_3e298875de25'));
                setIsSaving(false);
                return;
            }
        }

        if (!isValidUrl && !asset.url) {
            setIsSaving(false);
            return;
        }

        const newAsset = { ...asset };
        if (!newAsset.assetTypeId) {
            newAsset.assetTypeId = 4;
        }
        if (urlResponse) {
            newAsset.url = urlResponse;
        }
        newAsset.fileType = file.name.split('.').pop();
        newAsset.fileSize = formatFileSize(file.size);

        const createResponse = await createAsset(newAsset, null);

        if (createResponse.error) {
            Toast.error(createResponse.message);
            setIsSaving(false);
            return;
        }

        if (createResponse.status === 200 && createResponse.data.url) {
            Toast.success(getText('msg_9309271e92fe'));
            updateDocumentState({
                documentAsset: { assetId: createResponse.data.id },
                assetName: createResponse.data.name,
            });
            setIsUploadFromDeviceChecked('');
            setAsset({});
            setFile(null);
            setIsSaving(false);
            return;
        }

        Toast.error(getText('msg_9f8fd8f97fd3'));
        setIsSaving(false);
    }

    function validateForm() {
        if (!asset.name || asset.name.trim() === '') {
            Toast.error(getText('msg_d7659e2c4af2'));
            return false;
        }

        if (!file) {
            Toast.error(getText('msg_e11c5988bd25'));
            return false;
        }

        const fileExtension = file.name.split('.').pop().toLowerCase();
        if (BLOCKED_DOCUMENT_EXTENSIONS.includes(fileExtension)) {
            Toast.error(getText('msg_b389e75f4a4b'));
            return false;
        }

        const fileTypeAndSizeValidation = isValidFileTypeAndSize(file);
        if (fileTypeAndSizeValidation) {
            Toast.error(fileTypeAndSizeValidation);
            return false;
        }

        if (asset.name.length > 256) {
            Toast.error(getText('msg_f4231db74e51'));
            return false;
        }

        return true;
    }

    function updateAsset(assetObj, key, value) {
        return { ...assetObj, [key]: value };
    }

    const loadAssets = useCallback(
        async (page = currentPage, search = searchTerm) => {
            latestRequestIdRef.current += 1;
            const requestId = latestRequestIdRef.current;

            setIsLoading(true);
            setIsSaving(true);

            try {
                let queryParams = {
                    assetTypes: [1, 2],
                    page,
                    pageSize: 12,
                    fileTypes: SUPPORTED_DOCUMENT_TYPES,
                };

                if (search && search.trim() !== '') {
                    queryParams = { ...queryParams, search: search.trim() };
                }

                const fetchedAssets = await fetchAssets(
                    queryParams,
                    null,
                    false,
                );

                if (requestId !== latestRequestIdRef.current) {
                    return;
                }

                if (!fetchedAssets) {
                    setAssets([]);
                    setTotalPages(1);
                    return;
                }

                setAssets(fetchedAssets.viewModels || []);
                setTotalPages(fetchedAssets.pageTotal || 1);
            } catch (error) {
                console.error('Failed to fetch assets', error);
                Toast.error(getText('msg_cade149a85b2'));
            } finally {
                if (latestRequestIdRef.current === requestId) {
                    setIsLoading(false);
                    setIsSaving(false);
                }
            }
        },
        [currentPage, searchTerm, setIsSaving],
    );

    const debouncedLoadRef = useRef(
        debounce(term => {
            setCurrentPage(1);
            loadAssets(1, term);
        }, 400),
    );

    useEffect(() => {
        const debounced = debouncedLoadRef.current;
        return () => {
            debounced.cancel();
        };
    }, []);

    function handleSearch(event) {
        const value = event.target.value;
        setSearchTerm(value);
        debouncedLoadRef.current(value);

        if (value === '') {
            debouncedLoadRef.current.cancel();
            setCurrentPage(1);
            loadAssets(1, '');
        }
    }

    function handleSearchKeyDown(event) {
        if (event.key !== 'Enter') {
            return;
        }
        debouncedLoadRef.current.cancel();
        setCurrentPage(1);
        loadAssets(1, searchTerm);
    }

    useEffect(() => {
        if (isAddFromAssetsChecked) {
            loadAssets(currentPage, searchTerm);
        }
    }, [isAddFromAssetsChecked, currentPage]);

    function handleClick(index) {
        setActiveCard(activeCard === index ? null : index);
    }

    function addAsset() {
        if (activeCard === null) {
            Toast.error(
                getText('msg_c9d0d3fc9172'),
                null,
                getText('msg_40f0bdba0938'),
            );
            return;
        }

        const assetAdding = assets[activeCard];
        updateDocumentState({
            documentAsset: { assetId: assetAdding.id },
            assetName: assetAdding.name,
        });
        setIsAddFromAssetsChecked('');
        setSearchTerm('');
        setAssets([]);
        setTotalPages(1);
        setCurrentPage(1);
        setActiveCard(null);
    }

    function handleAssetFromLibrarySelected(assetAdding) {
        updateDocumentState({
            documentAsset: { assetId: assetAdding.id },
            assetName: assetAdding.name,
        });
        setIsAddFromAssetsChecked('');
        setSearchTerm('');
        setAssets([]);
        setTotalPages(1);
        setCurrentPage(1);
        setActiveCard(null);
    }

    function shouldHideForUploadOrAssets() {
        return isUploadFromDeviceChecked || isAddFromAssetsChecked;
    }

    function getCaptionCharacterCount() {
        const count = caption?.length || 0;
        return `Character Limit ${count}/200`;
    }

    function getCaptionCharacterStatus() {
        const count = caption?.length || 0;
        return count > 200 ? 'rgba(239, 78, 69, 1)' : 'rgba(47, 43, 67, 0.6)';
    }

    if (isLoading) {
        return <Spinner width={50} className={styles.loadingSpinner} />;
    }

    return (
        <>
            <div
                style={{
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    marginBottom: '20px',
                    display: shouldHideForUploadOrAssets() ? 'none' : 'block',
                }}
            >
                <div
                    style={{
                        fontSize: '16px',
                        fontWeight: '500',
                        color: 'rgba(47, 43, 67, 1)',
                    }}
                >
                    {getText('msg_e214b8a29923')}
                </div>
                <div
                    style={{
                        fontSize: '14px',
                        fontWeight: '400',
                        textAlign: 'left',
                        color: 'rgba(47, 43, 67, 0.6)',
                    }}
                >
                    {getText('msg_549de6949a18')}
                </div>
            </div>

            <div
                style={{
                    display:
                        areInputsDisabled || shouldHideForUploadOrAssets()
                            ? 'none'
                            : 'block',
                }}
            >
                <div
                    style={{
                        fontSize: '16px',
                        fontWeight: '500',
                        color: 'rgba(47, 43, 67, 1)',
                        marginBottom: '10px',
                    }}
                >
                    {getText('msg_2c3cafa4db3f')}
                    <span
                        style={{
                            color: '#EF4E45',
                            marginLeft: '4px',
                            fontWeight: '500',
                        }}
                    >
                        *
                    </span>
                </div>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <input
                        id="fileName"
                        style={{
                            height: '48px',
                            borderRadius: '12px',
                            border: 'solid 1px rgba(47, 43, 67, 0.2)',
                            paddingLeft: '16px',
                            paddingRight: '16px',
                        }}
                        className="form-control"
                        type="text"
                        placeholder={getText('msg_f76f1c1c6596')}
                        aria-label={getText('msg_f76f1c1c6596')}
                        value={assetName}
                        readOnly
                    />
                    <span
                        onClick={handleDocumentPopoverClick}
                        onKeyDown={e =>
                            e.key === 'Enter' && handleDocumentPopoverClick(e)
                        }
                        role="button"
                        tabIndex={0}
                        className="button-base ing-button-primary-left"
                        style={{ marginLeft: '20px' }}
                    >
                        <i className="bi bi-plus-lg me-1" />
                        {getText('msg_61cc55aa0453')}
                    </span>
                    <Popover
                        id={documentId}
                        open={documentOpen}
                        anchorEl={documentAnchorEl}
                        onClose={handlePopoverClose}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'grid',
                                gap: 0,
                                paddingBottom: 0,
                                paddingTop: 0,
                                lineHeight: '40px',
                            }}
                        >
                            <div
                                onClick={() => {
                                    setIsAddFromAssetsChecked('document');
                                    setIsUploadFromDeviceChecked('');
                                    setDocumentOpen(false);
                                }}
                                onKeyDown={e => {
                                    if (e.key === 'Enter') {
                                        setIsAddFromAssetsChecked('document');
                                        setIsUploadFromDeviceChecked('');
                                        setDocumentOpen(false);
                                    }
                                }}
                                role="button"
                                tabIndex={0}
                                className="pointer-cursor profile-link"
                                style={{ paddingRight: '80px' }}
                            >
                                {getText('msg_0bc609667aa7')}
                            </div>
                            <div
                                onClick={() => {
                                    setIsAddFromAssetsChecked('');
                                    setIsUploadFromDeviceChecked('document');
                                    setDocumentOpen(false);
                                }}
                                onKeyDown={e => {
                                    if (e.key === 'Enter') {
                                        setIsAddFromAssetsChecked('');
                                        setIsUploadFromDeviceChecked(
                                            'document',
                                        );
                                        setDocumentOpen(false);
                                    }
                                }}
                                role="button"
                                tabIndex={0}
                                className="pointer-cursor profile-link"
                                style={{ paddingRight: '80px' }}
                            >
                                {getText('msg_c002fe6df29c')}
                            </div>
                        </Box>
                    </Popover>
                </div>
                <div
                    style={{
                        minWidth: '300px',
                        paddingTop: '24px',
                    }}
                >
                    <div className="file-form-label">
                        {getText('msg_3428d97e6666')}
                    </div>
                    <textarea
                        onChange={handleCaptionChange}
                        value={caption}
                        className="form-control"
                        placeholder={getText('msg_e52f6ee57fa6')}
                        id="floatingTextarea"
                        style={{
                            height: '100px',
                            borderRadius: '12px',
                            border: 'solid 1px rgba(47, 43, 67, 0.2)',
                            resize: 'none',
                            padding: '16px',
                        }}
                        disabled={areInputsDisabled}
                    />
                    <div
                        style={{
                            fontSize: '14px',
                            fontWeight: '400',
                            textAlign: 'left',
                            color: getCaptionCharacterStatus(),
                            marginLeft: '8px',
                            marginTop: '8px',
                        }}
                    >
                        {getCaptionCharacterCount()}
                    </div>
                </div>
            </div>

            {isUploadFromDeviceChecked && (
                <>
                    <div style={{ width: '100%', paddingTop: '6px' }}>
                        <FileDrop file={file} onChange={handleFileChange} />
                    </div>

                    <InputField
                        label={getText('msg_709a23220f2c')}
                        onChange={value =>
                            setAsset(updateAsset(asset, 'name', value))
                        }
                        maxChars={256}
                        required
                        placeholder={getText('msg_b7ffb9789f09')}
                        value={asset.name}
                    />

                    <div style={{ marginBottom: '24px' }}>
                        <div className="file-form-label">
                            {getText('msg_848eed0fbd54')}
                            <Tooltip
                                html="Use relevant and specific keywords to help make searching for an asset easier."
                                placement="right"
                            >
                                <i
                                    className="bi bi-info-circle"
                                    style={{
                                        paddingLeft: '5px',
                                        fontSize: '13px',
                                        color: 'var(--iNGAGED-Primary-Color)',
                                    }}
                                />
                            </Tooltip>
                        </div>
                        <TagInput
                            onTagsChange={tags =>
                                setAsset(updateAsset(asset, 'assetTags', tags))
                            }
                            savedTags={asset.tags}
                        />
                    </div>

                    <div style={{ marginBottom: '24px' }}>
                        <SwitchField
                            label={getText('msg_0c359a10ae17')}
                            onChange={isChecked =>
                                setAsset(
                                    updateAsset(
                                        asset,
                                        'assetTypeId',
                                        isChecked ? 4 : 5,
                                    ),
                                )
                            }
                            tooltip={getText('msg_5abd905257ee')}
                            checked={asset.assetTypeId !== 5}
                        />
                    </div>

                    <div
                        id="footer"
                        style={{
                            height: 'fit-content',
                            width: '100%',
                            padding: '28px',
                        }}
                    >
                        <div
                            role="button"
                            tabIndex={0}
                            style={{
                                height: '40px',
                                width: '128px',
                                textAlign: 'center',
                                display: 'inline-block',
                            }}
                            className="float-start button-base button-secondary"
                            onClick={() => setIsUploadFromDeviceChecked('')}
                            onKeyDown={e =>
                                e.key === 'Enter' &&
                                setIsUploadFromDeviceChecked('')
                            }
                        >
                            {getText('msg_77dfd2135f4d')}
                        </div>
                        <div
                            role="button"
                            tabIndex={0}
                            style={{
                                height: '40px',
                                width: '128px',
                                textAlign: 'center',
                                display: 'inline-block',
                            }}
                            className="float-end button-base ing-button-primary-left"
                            onClick={uploadAsset}
                            onKeyDown={e => e.key === 'Enter' && uploadAsset()}
                        >
                            {getText('msg_61cc55aa0453')}
                        </div>
                    </div>
                </>
            )}

            {isAddFromAssetsChecked && (
                <AddFromAssetsModal
                    title="Add from Library"
                    onYes={handleAssetFromLibrarySelected}
                    onNo={() => {
                        setIsAddFromAssetsChecked('');
                    }}
                    onHide={() => {
                        setIsAddFromAssetsChecked('');
                    }}
                    yesButtonText="Add"
                    noButtonText="Cancel"
                    acceptedFileTypes={SUPPORTED_DOCUMENT_TYPES}
                    assetTypes={[1, 2]}
                    allowedFilterIds={['13', '1', '2', '14']}
                    wasCreatedByMe={false}
                    displayModalBackdrop={false}
                />
            )}
        </>
    );
}

export default NewPropertiesDocument;
